import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import { Document, Page } from "react-pdf";

import { handleMouseDown } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseDown";
import { handleMouseMove } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseMove";
import { handleMouseUp } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseUp";

import InteractionRectOverlay from "./overlays/InteractionRectOverlay";
import { createCanvasPointerScheduler } from "./canvasPointerScheduler";
import { createWidgetMovePreview } from "./widgetMovePreview";
import FieldToolPreviewOverlay from "./overlays/FieldToolPreviewOverlay";
import FormFieldOverlayLayer from "./overlays/FormFieldOverlayLayer";
import PdfTextOverlayLayer from "./overlays/PdfTextOverlayLayer";
import { canvasToPdfPoints } from "../../../../../../utils/coordinateUtils";
import { getEffectivePageRotation } from "../utils/documentOrientation";
import {
  copySelectedFieldGeometry,
  duplicateCopiedFields,
} from "../utils/fieldClipboard";

const createExtraFieldName = () => {
  if (typeof crypto !== "undefined" && crypto?.randomUUID) {
    return `extra_field_${crypto.randomUUID()}`;
  }

  return `extra_field_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const isEditableTarget = (target) => {
  if (!target || typeof target.closest !== "function") {
    return false;
  }

  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [contenteditable=""], [role="textbox"]',
    ),
  );
};

const hasNativeTextSelection = () => {
  if (typeof window === "undefined" || !window.getSelection) return false;
  const selection = window.getSelection();
  return Boolean(selection && selection.toString().length > 0);
};

const getSelectedTextWithinContainer = (container) => {
  if (!container || typeof window === "undefined" || !window.getSelection) {
    return "";
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return "";
  }

  const range = selection.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) {
    return "";
  }

  return selection.toString().replace(/\s+/g, " ").trim();
};

const StablePdfPage = React.memo(
  ({
    file,
    pageNumber,
    scale,
    displayRotation,
    canvasRef,
    pdfDocumentRef,
    onDocumentLoadSuccessRef,
    onPageLoadSuccessRef,
    buildPdfTextItemsForPageRef,
  }) => {
    return (
      <Document
        key={file ? "loaded" : "loading"}
        file={file}
        onLoadSuccess={(pdf) => onDocumentLoadSuccessRef.current?.(pdf)}
        onLoadError={(e) => console.error("Document load error:", e)}
        ref={pdfDocumentRef}
      >
        <Page
          pageNumber={pageNumber}
          canvasRef={canvasRef}
          scale={scale}
          rotate={displayRotation}
          renderTextLayer
          renderAnnotationLayer
          className="custom-react-pdf"
          onLoadSuccess={(page) => onPageLoadSuccessRef.current?.(page)}
          onRenderTextLayerSuccess={() =>
            buildPdfTextItemsForPageRef.current?.()
          }
          onLoadError={(error) => console.error("Page load error:", error)}
        />
      </Document>
    );
  },
  (prev, next) =>
    prev.file === next.file &&
    prev.pageNumber === next.pageNumber &&
    prev.scale === next.scale &&
    prev.displayRotation === next.displayRotation,
);

StablePdfPage.displayName = "StablePdfPage";

const PDFCanvas = ({
  base64File,
  pdfInput,
  pdfScrollContainerRef,
  pdfContainerRef,
  pdfDocumentRef,
  currentProcessingFile,
  setCurrentProcessingFile,
  pageNumber,
  scale,
  displayRotation,
  selectToolActive,
  typingToolActive,
  highlightFields,
  textOverlayDisplayMode,
  focusedField,
  setFocusedField,
  currCursor,
  unassignedFieldNames,
  fixedInputs,
  inputRef,
  childInputRef,
  resizing,
  setResizing,
  moving,
  setMoving,
  isDrawing,
  setIsDrawing,
  isSelecting,
  setIsSelecting,
  rect,
  setRect,
  selectionRect,
  setSelectionRect,
  freeSelectStartPoint,
  setFreeSelectStartPoint,
  startPoint,
  setStartPoint,
  initialMousePosition,
  setInitialMousePosition,
  initialInputSize,
  setInitialInputSize,
  inputPosition,
  setInputPosition,
  resizeDirection,
  setResizeDirection,
  setCurrCursor,
  onDocumentLoadSuccess,
  onPageLoadSuccess,
  handleFieldChange,
  undoRedoStats,
  activeEditorTool,
  handleEditorToolSelection,
  textSelectionMode,
  formFields,
  onNavigateToTaggedField,
  onSearchSelectedText,
  onFavouriteDrop,
  favouriteDropDisabled = false,
}) => {
  const scaleFactor =
    currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor ||
    1;
  const pageProxyRef = useRef(null);
  const extractedTextLayerKeyRef = useRef(null);
  const canvasRef = useRef(null);
  const copiedFieldGeometriesRef = useRef([]);

  const [canvasSize, setCanvasSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;

      if (width === 0 || height === 0) return;

      setCanvasSize((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height },
      );
    });

    ro.observe(canvas);
    return () => ro.disconnect();
  }, [scale, pageNumber]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ""; // Chrome requires returnValue to be set

      return ""; // Some browsers require a return value
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const guardPushed = useRef(false);

  useEffect(() => {
    if (!guardPushed.current) {
      window.history.pushState(null, "", window.location.href);
      guardPushed.current = true;
    }

    const handlePopState = () => {
      const leave = window.confirm("Are you sure you want to leave this page?");
      if (leave) {
        window.removeEventListener("popstate", handlePopState);
        window.history.back();
      } else {
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const onPageLoadSuccessRef = useRef((page) => {
    pageProxyRef.current = page;
    extractedTextLayerKeyRef.current = null;
    onPageLoadSuccess(page);
  });

  useLayoutEffect(() => {
    onPageLoadSuccessRef.current = (page) => {
      pageProxyRef.current = page;
      extractedTextLayerKeyRef.current = null;
      onPageLoadSuccess(page);
    };
  });

  useEffect(() => {
    if (!pageProxyRef.current) return;

    extractedTextLayerKeyRef.current = null;
    onPageLoadSuccessRef.current?.(pageProxyRef.current);
  }, [displayRotation]);

  const onDocumentLoadSuccessRef = useRef(onDocumentLoadSuccess);
  useLayoutEffect(() => {
    onDocumentLoadSuccessRef.current = onDocumentLoadSuccess;
  });

  const buildPdfTextItemsForPage = useCallback(() => {
    const textLayer = pdfContainerRef?.current?.querySelector(
      ".react-pdf__Page__textContent",
    );
    const pageNode =
      pdfContainerRef?.current?.querySelector(".react-pdf__Page");
    const page = pageProxyRef.current;
    if (!textLayer || !pageNode || !page?.getViewport) return;

    const viewport = page.getViewport({
      scale,
      rotation: getEffectivePageRotation(page.rotate, displayRotation),
    });
    const extractionKey = `${pageNumber}_${scale}_${Math.round(viewport.width)}_${Math.round(viewport.height)}`;

    if (extractedTextLayerKeyRef.current === extractionKey) {
      return;
    }

    const pageRect = pageNode.getBoundingClientRect();
    const spans = Array.from(textLayer.querySelectorAll("span"));
    const items = [];

    spans.forEach((span, spanIndex) => {
      const text = (span.textContent || "").replace(/\s+/g, " ").trim();
      if (!text) return;

      const spanRect = span.getBoundingClientRect();
      const relativeLeft = spanRect.left - pageRect.left;
      const relativeTop = spanRect.top - pageRect.top;
      const relativeWidth = spanRect.width;
      const relativeHeight = spanRect.height;

      const runRect = canvasToPdfPoints(
        relativeLeft,
        relativeTop,
        relativeWidth,
        relativeHeight,
        viewport,
      );

      items.push({
        id: `run_${pageNumber}_${spanIndex}`,
        page: pageNumber,
        kind: "run",
        text,
        rect: {
          X: runRect.x,
          Y: runRect.y,
          W: runRect.width,
          H: runRect.height,
        },
      });

      const words = text.split(/\s+/).filter(Boolean);
      const totalCharacters = words.join("").length || text.length || 1;
      let offsetChars = 0;

      words.forEach((word, wordIndex) => {
        const wordChars = word.length || 1;
        const wordWidthPx = (relativeWidth * wordChars) / totalCharacters;
        const wordLeftPx =
          relativeLeft + (relativeWidth * offsetChars) / totalCharacters;
        offsetChars += wordChars;

        const wordRect = canvasToPdfPoints(
          wordLeftPx,
          relativeTop,
          wordWidthPx,
          relativeHeight,
          viewport,
        );

        items.push({
          id: `word_${pageNumber}_${spanIndex}_${wordIndex}`,
          page: pageNumber,
          kind: "word",
          text: word,
          rect: {
            X: wordRect.x,
            Y: wordRect.y,
            W: wordRect.width,
            H: wordRect.height,
          },
        });
      });
    });

    setCurrentProcessingFile((prev) => ({
      ...prev,
      document: {
        ...prev.document,
        tempFields: {
          ...prev.document.tempFields,
          pdfTextItemsByPage: {
            ...(prev.document.tempFields.pdfTextItemsByPage || {}),
            [pageNumber]: items,
          },
        },
      },
    }));

    extractedTextLayerKeyRef.current = extractionKey;
  }, [
    displayRotation,
    pageNumber,
    pdfContainerRef,
    scale,
    setCurrentProcessingFile,
  ]);

  const buildPdfTextItemsForPageRef = useRef(buildPdfTextItemsForPage);
  useLayoutEffect(() => {
    buildPdfTextItemsForPageRef.current = buildPdfTextItemsForPage;
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const ctrl = isMac ? e.metaKey : e.ctrlKey;
      const isTypingInEditable = isEditableTarget(e.target);
      const isEscToEditTool = [
        "checkbox",
        "textField",
        "insertText",
        "rectangle",
        "line",
      ].includes(activeEditorTool);
      const tempFields = currentProcessingFile?.document?.tempFields;
      const selectedFields = copySelectedFieldGeometry(tempFields, pageNumber);
      const key = e.key?.toLowerCase();
      const isWithinPdfCanvas = pdfContainerRef.current?.contains(e.target);

      if (
        e.shiftKey &&
        !ctrl &&
        key === "f" &&
        !isTypingInEditable &&
        textSelectionMode &&
        selectToolActive
      ) {
        const selectedPdfText = [
          tempFields?.selectedPdfTextItem,
          ...(tempFields?.selectedExtraPdfTextItems || []),
        ]
          .map((item) => item?.text || "")
          .filter(Boolean)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();
        const selectedText =
          selectedPdfText ||
          getSelectedTextWithinContainer(pdfContainerRef.current);

        if (selectedText) {
          e.preventDefault();
          onSearchSelectedText?.(selectedText);
          return;
        }
      }

      if (
        ctrl &&
        key === "c" &&
        activeEditorTool === "edit" &&
        selectedFields.length > 0 &&
        !hasNativeTextSelection() &&
        isWithinPdfCanvas
      ) {
        e.preventDefault();
        copiedFieldGeometriesRef.current = selectedFields;

        return;
      }

      if (
        ctrl &&
        key === "v" &&
        activeEditorTool === "edit" &&
        copiedFieldGeometriesRef.current.length > 0 &&
        !hasNativeTextSelection() &&
        isWithinPdfCanvas
      ) {
        e.preventDefault();

        setCurrentProcessingFile((prevFile) => {
          const prevTempFields = prevFile?.document?.tempFields || {};
          const newFields = duplicateCopiedFields({
            copiedFields: copiedFieldGeometriesRef.current,
            existingFields: prevTempFields.formFields || [],
            fallbackPage: pageNumber,
            createName: createExtraFieldName,
          });

          if (newFields.length === 0) return prevFile;

          return {
            ...prevFile,
            document: {
              ...prevFile.document,
              tempFields: {
                ...prevTempFields,
                formFields: [
                  ...(prevTempFields.formFields || []),
                  ...newFields,
                ],
                selectedFormField: newFields[0],
                selectedExtraFields: newFields,
                selectedPdfTextItem: null,
                selectedExtraPdfTextItems: [],
              },
            },
          };
        });

        return;
      }

      if (e.key === "Escape" && isEscToEditTool && !isTypingInEditable) {
        e.preventDefault();
        handleEditorToolSelection?.("edit");
      } else if (isTypingInEditable) {
        return;
      } else if (ctrl && key === "z" && !e.shiftKey) {
        e.preventDefault();
        undoRedoStats?.undo();
      } else if (ctrl && (key === "y" || (key === "z" && e.shiftKey))) {
        e.preventDefault();
        undoRedoStats?.redo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeEditorTool,
    currentProcessingFile,
    handleEditorToolSelection,
    onSearchSelectedText,
    pageNumber,
    selectToolActive,
    setCurrentProcessingFile,
    textSelectionMode,
    undoRedoStats,
    pdfContainerRef,
  ]);

  const interactionRectRef = useRef(null);
  const latestDrawingRectRef = useRef(rect);
  useLayoutEffect(() => {
    latestDrawingRectRef.current = rect;
  }, [rect, isDrawing]);

  const setDrawingRectForFrame = useCallback((nextRect) => {
    latestDrawingRectRef.current = nextRect;
    interactionRectRef.current?.setDrawingRect(nextRect);
  }, []);
  const setSelectionRectForFrame = useCallback((nextRect) => {
    interactionRectRef.current?.setSelectingRect(nextRect);
  }, []);

  const widgetMovePreviewRef = useRef(null);
  const widgetMoveControllerRef = useRef(null);
  if (!widgetMoveControllerRef.current) {
    widgetMoveControllerRef.current = createWidgetMovePreview();
  }

  const pointerHandlersRef = useRef(null);
  useLayoutEffect(() => {
    pointerHandlersRef.current = {
      active: resizing || moving || isDrawing || isSelecting,
      move: (point) => {
        // Move/resize frames stay local; shared geometry is committed on release.
        const preview = widgetMovePreviewRef.current;
        const isBoxSelection =
          isSelecting && !moving && !resizing && !isDrawing;

        const useLocalMove = (moving || resizing || isBoxSelection) && preview;

        const controller = widgetMoveControllerRef.current;

        const gestureKey = isBoxSelection
          ? freeSelectStartPoint
          : initialMousePosition;

        const file = useLocalMove
          ? controller.read(
              currentProcessingFile,
              gestureKey,
              isBoxSelection ? "selection" : "geometry",
            )
          : currentProcessingFile;

        const updateFile = useLocalMove
          ? (updater) => preview.setFile(controller.update(updater))
          : setCurrentProcessingFile;
        handleMouseMove(
          point,
          file,
          updateFile,
          pdfContainerRef,
          resizing,
          initialMousePosition,
          initialInputSize,
          inputPosition,
          resizeDirection,
          moving,
          isDrawing,
          startPoint,
          setDrawingRectForFrame,
          isSelecting,
          freeSelectStartPoint,
          setSelectionRectForFrame,
          pageNumber,
        );
      },
      finish: () => {
        let pendingFile = currentProcessingFile;
        let historyRecorded = false;

        const isEditingGesture = moving || resizing || isDrawing;

        const commitWithHistory = (update) => {
          const previousFile = pendingFile;

          // Check the result before submitting this update to React.
          pendingFile =
            typeof update === "function" ? update(previousFile) : update;

          const fieldsChanged =
            pendingFile?.document?.tempFields?.formFields !==
            previousFile?.document?.tempFields?.formFields;

          if (isEditingGesture && fieldsChanged && !historyRecorded) {
            undoRedoStats?.pushHistory?.();
            historyRecorded = true;
          }

          setCurrentProcessingFile(update);
        };

        widgetMoveControllerRef.current.commit(commitWithHistory);
        widgetMovePreviewRef.current?.setFile(null);

        handleMouseUp(
          currentProcessingFile,
          commitWithHistory,
          resizing,
          setResizing,
          setCurrCursor,
          moving,
          setMoving,
          isDrawing,
          latestDrawingRectRef.current,
          setIsDrawing,
          setRect,
          fixedInputs,
          isSelecting,
          setIsSelecting,
          setSelectionRect,
          setFreeSelectStartPoint,
        );
      },
    };
  });
  const pointerSchedulerRef = useRef(null);
  if (!pointerSchedulerRef.current) {
    pointerSchedulerRef.current = createCanvasPointerScheduler((point) => {
      const handlers = pointerHandlersRef.current;
      if (handlers?.active) handlers.move(point);
    });
  }

  useEffect(() => {
    const scheduler = pointerSchedulerRef.current;
    const finishInteraction = (event) => {
      const handlers = pointerHandlersRef.current;
      if (!handlers?.active) return;
      // Include a release inside the canvas, even if it precedes the next frame.
      if (
        event.type === "mouseup" &&
        pdfContainerRef.current?.contains(event.target)
      ) {
        scheduler.queue(event);
      }
      scheduler.flush();
      handlers.finish();
      handlers.active = false;
    };
    window.addEventListener("mouseup", finishInteraction);
    window.addEventListener("blur", finishInteraction);
    return () => {
      scheduler.cancel();
      widgetMoveControllerRef.current.cancel();
      window.removeEventListener("mouseup", finishInteraction);
      window.removeEventListener("blur", finishInteraction);
    };
  }, [pdfContainerRef]);

  // A queued event from the old page must not move fields on a new page.
  useLayoutEffect(() => {
    if (widgetMoveControllerRef.current.cancel()) {
      widgetMovePreviewRef.current?.setFile(null);
      setMoving(false);
      setResizing(false);
      setIsSelecting(false);
      setSelectionRect(null);
      setFreeSelectStartPoint(null);
      setCurrCursor("default");
    }

    return () => pointerSchedulerRef.current.cancel();
  }, [
    base64File,
    pdfInput,
    pageNumber,
    scale,
    displayRotation,
    setMoving,
    setResizing,
    setIsSelecting,
    setSelectionRect,
    setFreeSelectStartPoint,
    setCurrCursor,
  ]);

  useLayoutEffect(() => {
    pointerHandlersRef.current = {
      active: resizing || moving || isDrawing || isSelecting,
      move: (point) => {
        // Move/resize frames stay local; shared geometry is committed on release.
        const preview = widgetMovePreviewRef.current;
        const isBoxSelection =
          isSelecting && !moving && !resizing && !isDrawing;

        const useLocalMove = (moving || resizing || isBoxSelection) && preview;

        const controller = widgetMoveControllerRef.current;

        const gestureKey = isBoxSelection
          ? freeSelectStartPoint
          : initialMousePosition;

        const file = useLocalMove
          ? controller.read(
              currentProcessingFile,
              gestureKey,
              isBoxSelection ? "selection" : "geometry",
            )
          : currentProcessingFile;

        const updateFile = useLocalMove
          ? (updater) => preview.setFile(controller.update(updater))
          : setCurrentProcessingFile;
        handleMouseMove(
          point,
          file,
          updateFile,
          pdfContainerRef,
          resizing,
          initialMousePosition,
          initialInputSize,
          inputPosition,
          resizeDirection,
          moving,
          isDrawing,
          startPoint,
          setDrawingRectForFrame,
          isSelecting,
          freeSelectStartPoint,
          setSelectionRectForFrame,
          pageNumber,
        );
      },
      finish: () => {
        widgetMoveControllerRef.current.commit(setCurrentProcessingFile);
        widgetMovePreviewRef.current?.setFile(null);
        handleMouseUp(
          currentProcessingFile,
          setCurrentProcessingFile,
          resizing,
          setResizing,
          setCurrCursor,
          moving,
          setMoving,
          isDrawing,
          latestDrawingRectRef.current,
          setIsDrawing,
          setRect,
          fixedInputs,
          isSelecting,
          setIsSelecting,
          setSelectionRect,
          setFreeSelectStartPoint,
        );
      },
    };
  });

  if (!pointerSchedulerRef.current) {
    pointerSchedulerRef.current = createCanvasPointerScheduler((point) => {
      const handlers = pointerHandlersRef.current;
      if (handlers?.active) handlers.move(point);
    });
  }

  useEffect(() => {
    const scheduler = pointerSchedulerRef.current;
    const finishInteraction = (event) => {
      const handlers = pointerHandlersRef.current;
      if (!handlers?.active) return;
      // Include a release inside the canvas, even if it precedes the next frame.
      if (
        event.type === "mouseup" &&
        pdfContainerRef.current?.contains(event.target)
      ) {
        scheduler.queue(event);
      }
      scheduler.flush();
      handlers.finish();
      handlers.active = false;
    };
    window.addEventListener("mouseup", finishInteraction);
    window.addEventListener("blur", finishInteraction);
    return () => {
      scheduler.cancel();
      widgetMoveControllerRef.current.cancel();
      window.removeEventListener("mouseup", finishInteraction);
      window.removeEventListener("blur", finishInteraction);
    };
  }, [pdfContainerRef]);

  // A queued event from the old page must not move fields on a new page.
  useLayoutEffect(() => {
    if (widgetMoveControllerRef.current.cancel()) {
      widgetMovePreviewRef.current?.setFile(null);
      setMoving(false);
      setResizing(false);
      setIsSelecting(false);
      setSelectionRect(null);
      setFreeSelectStartPoint(null);
      setCurrCursor("default");
    }

    return () => pointerSchedulerRef.current.cancel();
  }, [
    base64File,
    pdfInput,
    pageNumber,
    scale,
    displayRotation,
    setMoving,
    setResizing,
    setIsSelecting,
    setSelectionRect,
    setFreeSelectStartPoint,
    setCurrCursor,
  ]);

  if (!(base64File || pdfInput)) return null;

  // console.log(canvasSize)

  return (
    <Box
      ref={pdfScrollContainerRef}
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        minHeight: 0,
        alignSelf: "stretch",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "flex-start",
        overflow: "auto",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          margin: "auto",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(228, 228, 228, 0.5)",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Box
          ref={pdfContainerRef}
          sx={{
            position: "relative",
            display: "inline-block",
            border: "2px solid #d3d3d3",
            borderRadius: "18px",
            boxShadow: "3px 4px 12px rgba(0, 0, 0, 0.3)",
            backgroundColor: "#fff",
            cursor: currCursor || "default",
            userSelect: "none",
            width: canvasSize.width ?? "auto",
            height: canvasSize.height ?? "auto",
          }}
          onMouseDown={(e) => {
            const isToolActive = Object.values(
              currentProcessingFile?.document?.tempFields?.formFieldTools || {},
            ).some((field) => field.active);

            if (isToolActive) {
              handleMouseDown(
                e,
                currentProcessingFile,
                setCurrentProcessingFile,
                null,
                null,
                "fieldTools",
                "crosshair",
                pdfContainerRef,
                setStartPoint,
                setRect,
                setIsDrawing,
                setCurrCursor,
                setFreeSelectStartPoint,
                setMoving,
                setIsSelecting,
                setInitialMousePosition,
                setInitialInputSize,
                setInputPosition,
                setSelectionRect,
                setResizing,
                setResizeDirection,
                pageNumber,
              );
            } else if (selectToolActive) {
              handleMouseDown(
                e,
                currentProcessingFile,
                setCurrentProcessingFile,
                null,
                null,
                "freeSelect",
                "default",
                pdfContainerRef,
                setStartPoint,
                setRect,
                setIsDrawing,
                setCurrCursor,
                setFreeSelectStartPoint,
                setMoving,
                setIsSelecting,
                setInitialMousePosition,
                setInitialInputSize,
                setInputPosition,
                setSelectionRect,
                setResizing,
                setResizeDirection,
                pageNumber,
              );
            }
          }}
          onMouseMove={(e) => {
            if (resizing || moving || isDrawing || isSelecting) {
              pointerSchedulerRef.current.queue(e);
            }
          }}
        >
          {/* StablePdfPage only re-renders when file/pageNumber/scale change,
              never when currentProcessingFile changes due to field interactions.
              This prevents pdf.js from aborting its async TextLayer render task. */}
          <StablePdfPage
            file={base64File || pdfInput}
            pageNumber={pageNumber}
            scale={scale}
            displayRotation={displayRotation}
            canvasRef={canvasRef}
            pdfDocumentRef={pdfDocumentRef}
            onDocumentLoadSuccessRef={onDocumentLoadSuccessRef}
            onPageLoadSuccessRef={onPageLoadSuccessRef}
            buildPdfTextItemsForPageRef={buildPdfTextItemsForPageRef}
          />

          <InteractionRectOverlay
            ref={interactionRectRef}
            isDrawing={isDrawing}
            isSelecting={isSelecting}
            rect={rect}
            selectionRect={selectionRect}
            scaleFactor={scaleFactor}
            activeEditorTool={activeEditorTool}
          />

          <FieldToolPreviewOverlay
            fields={
              currentProcessingFile?.document?.tempFields?.formFieldTools
                ?.textField?.values || []
            }
          />

          <PdfTextOverlayLayer
            currentProcessingFile={currentProcessingFile}
            pageNumber={pageNumber}
            selectToolActive={selectToolActive}
            highlightFields={highlightFields}
            setCurrentProcessingFile={setCurrentProcessingFile}
            textSelectionMode={textSelectionMode}
          />

          <FormFieldOverlayLayer
            widgetMovePreviewRef={widgetMovePreviewRef}
            currentProcessingFile={currentProcessingFile}
            pageNumber={pageNumber}
            scale={scale}
            selectToolActive={selectToolActive}
            typingToolActive={typingToolActive}
            highlightFields={highlightFields}
            textOverlayDisplayMode={textOverlayDisplayMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            currCursor={currCursor}
            unassignedFieldNames={unassignedFieldNames}
            fixedInputs={fixedInputs}
            inputRef={inputRef}
            childInputRef={childInputRef}
            pdfContainerRef={pdfContainerRef}
            setCurrentProcessingFile={setCurrentProcessingFile}
            setStartPoint={setStartPoint}
            setRect={setRect}
            setIsDrawing={setIsDrawing}
            setCurrCursor={setCurrCursor}
            setFreeSelectStartPoint={setFreeSelectStartPoint}
            setMoving={setMoving}
            setIsSelecting={setIsSelecting}
            setInitialMousePosition={setInitialMousePosition}
            setInitialInputSize={setInitialInputSize}
            setInputPosition={setInputPosition}
            setSelectionRect={setSelectionRect}
            setResizing={setResizing}
            setResizeDirection={setResizeDirection}
            handleMouseDown={handleMouseDown}
            handleFieldChange={handleFieldChange}
            undoRedoStats={undoRedoStats}
            activeEditorTool={activeEditorTool}
            formFields={formFields}
            onNavigateToTaggedField={onNavigateToTaggedField}
            onFavouriteDrop={onFavouriteDrop}
            favouriteDropDisabled={favouriteDropDisabled}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PDFCanvas;
